import isNil from 'lodash/isNil';
import { createSelector } from 'reselect';
import { DisabilityInsuranceProgressBarChart } from '../../../../../js/app/react/chartDescriptors.financialPlan';
import { selectedIndex as selectedOwnerIndex } from '../../OwnerDropdown/OwnerDropdownSelector';
import { computeChartIndex, computeChartStringKey } from './ProgressChartHelpers';

const BASE_LOCAL_DATA = {
    localChartData: null,
    localGroup: null,
    localGoalData: null,
    localResponseData: null,
  },
  dictionary = {};

const renderChartLabels = key =>
    function() {
      const { coverageLimit, monthlySalary, currentCoverage, passiveIncome } = dictionary[
        key
      ].localGoalData;

      //create a group so they're all together, and so that we don't re-draw the same group.
      if (dictionary[key].localGroup) {
        dictionary[key].localGroup.destroy();
      }

      dictionary[key].localGroup = this.renderer
        .g()
        .attr({
          zIndex: 10,
          id: 'chart-labels',
        })
        .add();

      let text_y, amount;

      /* MONTHLY SALARY LABEL */

      if (monthlySalary.value > 0) {
        let bbox = this.series[monthlySalary.ordinal].data[0].graphic.element.getBBox(),
          current_label_point = {
            x: this.series[monthlySalary.ordinal].data[0].plotX + this.plotLeft,
            y: this.series[monthlySalary.ordinal].data[0].plotY + this.plotTop,
          };
        text_y = current_label_point.y - 5;

        //render the label
        amount = 'MONTHLY SALARY $' + ADV.util.number_format(monthlySalary.value);
        this.renderer
          .text('<span>' + amount + '</span>', current_label_point.x, text_y)
          .attr({
            align: 'center',
            width: bbox.width,
          })
          .css({
            fontSize: '15px',
            fontWeight: '700',
            color: '#5abee4',
            fontFamily: 'Open Sans, helvetica, sans-serif',
            lineHeight: '15px',
          })
          .add(dictionary[key].localGroup);
      }

      /* COVERAGE LIMIT LINE */
      const bbox = this.series[coverageLimit.ordinal].data[0].graphic.element.getBBox(),
        coverageLimitLine = {
          x1:
            this.plotLeft +
            this.series[coverageLimit.ordinal].data[0].series.points[0].plotX -
            bbox.width / 2,
          x2:
            this.plotLeft +
            this.series[coverageLimit.ordinal].data[0].series.points[0].plotX +
            bbox.width / 2,
          y1: this.plotTop + this.series[coverageLimit.ordinal].data[0].series.points[0].plotY,
          y2: this.plotTop + this.series[coverageLimit.ordinal].data[0].series.points[0].plotY,
        };

      //define the dashed line SVG
      const path = [
        'M',
        coverageLimitLine.x1,
        coverageLimitLine.y1,
        'L',
        coverageLimitLine.x2,
        coverageLimitLine.y2,
      ];

      //render the line
      this.renderer
        .path(path)
        .attr({
          'stroke-width': 3,
          stroke: '#1b85ae',
          dashstyle: 'shortdash',
        })
        .add(dictionary[key].localGroup);
    },
  getPoint = (key, series) => {
    const index = series[0].findIndex(value => value === key);
    return {
      name: key,
      ordinal: index,
      value: series[1][index],
    };
  },
  getColor = (index, coverageLimit) => {
    switch (index) {
      case 3:
        return ADV.settings.color_groups[0][6];
      case 2:
        return ADV.settings.color_groups[0][0];
      case 1:
      case !!coverageLimit && coverageLimit.ordinal:
        return '#e5f8ff';
      case 0:
        return '#FFFFFF';
    }
  },
  renderLegendValues = () => {
    const { coverageLimit, monthlySalary, currentCoverage, passiveIncome } = dictionary[
      key
    ].localGoalData;

    $scope
      .find('.cur_coverage')
      .html('$' + ADV.util.number_format(cur_coverage.value - (pas_income ? pas_income.value : 0)));
    $scope
      .find('.cov_gap')
      .html('$' + ADV.util.number_format(cov_limit.value - cur_coverage.value));
    $scope.find('.cov_limit').html('$' + ADV.util.number_format(cov_limit.value));

    if (pas_income.value >= 1) {
      $scope.find('.pas_income').html('$' + ADV.util.number_format(pas_income.value));
    } else {
      $scope
        .find('.pas_income')
        .parent()
        .hide();
    }
  };

export const responseDataSelector = (state, props) => {
    const ownerIndex = !isNil(props.ownerIndex)
        ? props.ownerIndex
        : selectedOwnerIndex(state, props),
      chartIndex = computeChartIndex(ownerIndex === 1);

    return {
      ...props,
      ownerIndex,
      chartIndex,
    };
  },
  goalDataSelector = (state, props) => {
    const responseData = responseDataSelector(state, props),
      data = responseData.subgoalData.GoalCharts[responseData.chartIndex];
    return {
      ownerIndex: responseData.ownerIndex,
      responseData: data,
      coverageLimit: getPoint('Coverage Limit', data.Data.Series),
      monthlySalary: getPoint('Monthly Salary', data.Data.Series),
      currentCoverage: getPoint('Current Coverage', data.Data.Series),
      passiveIncome: getPoint('Passive Income', data.Data.Series),
    };
  },
  chartDataSelector = createSelector(
    [
      (state, props) => {
        const goalData = goalDataSelector(state, props);
        return {
          ...goalData,
          isPrint: props.isPrint,
          key: computeChartStringKey(
            goalData.ownerIndex.toString(),
            `${props.subgoalId}`,
            props.fpId,
            props.clientId,
          ),
        };
      },
    ],
    ({
      responseData,
      coverageLimit,
      monthlySalary,
      currentCoverage,
      passiveIncome,
      isPrint,
      key,
    }) => {
      if (responseData == null || Object.keys(responseData).length < 1) {
        return null;
      }

      if (!dictionary[key]) {
        dictionary[key] = clone(BASE_LOCAL_DATA);
      }

      if (ADV.util.deepEqual(responseData, dictionary[key].localResponseData)) {
        return dictionary[key].localChartData;
      }

      dictionary[key].localGoalData = {
        coverageLimit,
        monthlySalary,
        currentCoverage,
        passiveIncome,
      };
      const chartData = clone(DisabilityInsuranceProgressBarChart),
        localGoalData = dictionary[key].localGoalData;

      chartData.plotOptions.series.pointWidth = document.body.clientWidth > 768 ? 220 : 150;

      Object.keys(localGoalData).forEach(key => {
        const { ordinal, name, value } = localGoalData[key];
        const color = getColor(ordinal, localGoalData.coverageLimit);

        chartData.series[ordinal] = {
          data: [
            {
              y: value,
            },
          ],
          name,
          color,
          borderWidth: 0,
        };
      });

      // Pass the passive income value to the current coverage
      // to provide the correct tooltip.
      chartData.series.find(function(series) {
        return series.name === 'Current Coverage';
      }).data[0]['pas_income_value'] = passiveIncome.value;

      Object.assign(chartData.series[0], {
        borderWidth: 2,
        pointWidth: document.documentElement.clientWidth <= 768 ? 152 : 222,
      });

      if (responseData.Data.IsEmpty) {
        chartData.yAxis.max = 10000;
      }

      if (isPrint) {
        chartData.plotOptions.series.pointWidth = 150;
        Object.assign(chartData.series[0], {
          borderWidth: 2,
          pointWidth: 152,
        });
        chartData.series[localGoalData.coverageLimit.ordinal].color = '#e5f8ff';
        chartData.series.forEach(serie => {
          serie.animation = false;
        });
      }

      chartData.chart.events = {
        load: renderChartLabels(key),
        redraw: renderChartLabels(key),
      };
      dictionary[key].localResponseData = responseData;
      dictionary[key].localChartData = chartData;
      return chartData;
    },
  );
